import React from "react";
import { Edge, Node, NodeProps, useEdges, useReactFlow } from "reactflow";
import axios from "axios";
import { operationType, selectDataType } from "../../../../../services/types";
import { Main } from "./style";
import { webURL } from "../../../../../services/API";
import { store } from '../../../../../redux/store';
import { addUpdateNodeToQueue, updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
// import { Select } from "../../../../form/select";
// import { TextInput } from "../../../../form/textInput";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
// import { Styled } from "./style";
import {
   CheckCircle2, XCircle, FileText, Link2, AlertTriangle,
   RefreshCw, UploadCloud, X, Database, MessageSquare,
   FileCode, Settings2, FolderOpen, Loader2, Zap
} from 'lucide-react';

export const allowedFileTypesDocSearch = [
   'application/pdf', 'application/msword',
   'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
   'text/plain', 'text/csv'
];

// RAG Studio data type
interface RagStudioData {
   id: string;
   isIndexed: boolean;
   indexingStatus: string;
   files: { type: string; name: string; url: string }[];
}

// RAG Entity list item
interface RagEntityItem {
   _id: string;
   name: string;
   description?: string;
   isIndexed: boolean;
   indexingStatus: string;
   files: any[];
   urls: any[];
   apis: { url: string; name: string; responseField?: string }[];
}

type ragProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const cardStyle: React.CSSProperties = {
   background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
   marginBottom: '10px', overflow: 'hidden'
};
const cardHeaderStyle: React.CSSProperties = {
   padding: '10px 14px', borderBottom: '1px solid #f3f4f6', background: '#fafafa',
   display: 'flex', alignItems: 'center', justifyContent: 'space-between'
};
const labelStyle: React.CSSProperties = {
   fontSize: '11px', fontWeight: 700, color: '#374151',
   textTransform: 'uppercase', letterSpacing: '0.6px'
};
const subLabelStyle: React.CSSProperties = {
   fontSize: '10px', fontWeight: 600, color: '#9ca3af',
   textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '7px'
};
const selectStyle: React.CSSProperties = {
   width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb',
   borderRadius: '8px', fontSize: '13px', outline: 'none',
   background: '#fafafa', cursor: 'pointer', color: '#374151'
};
const ghostBtnStyle: React.CSSProperties = {
   display: 'inline-flex', alignItems: 'center', gap: '4px',
   background: 'none', border: 'none', fontSize: '11px', color: '#7c3aed',
   cursor: 'pointer', fontWeight: 600, padding: '2px 4px', borderRadius: '4px'
};
const fileRowStyle: React.CSSProperties = {
   display: 'flex', alignItems: 'center', justifyContent: 'space-between',
   padding: '7px 10px', background: '#f0fdf4', borderRadius: '6px', fontSize: '12px'
};
const fileNameStyle: React.CSSProperties = {
   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
   maxWidth: '260px', color: '#374151'
};
const deleteRowBtnStyle: React.CSSProperties = {
   background: 'none', border: 'none', color: '#ef4444',
   cursor: 'pointer', padding: '2px', display: 'flex',
   alignItems: 'center', opacity: 0.7, flexShrink: 0
};
const fieldLabelStyle: React.CSSProperties = {
   display: 'block', fontSize: '10px', fontWeight: 700, color: '#6b7280',
   marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.5px'
};

export function DocSearch({ id, data, setChildMinimize, setChildSettings }: ragProps) {
   const [isDragFile, setIsDragFile] = React.useState(false);
   const [requests, setRequests] = React.useState<selectDataType[]>([
      { id: 1, name: 'With File', refId: 'with_file', isActive: true }, { id: 2, name: 'Excel to database', refId: 'excel_database', isActive: true },
      { id: 3, name: 'On-Premise Database', refId: 'on_prem' }, { id: 4, name: 'Workflow Database', refId: 'workflow_database' },
      { id: 5, name: 'RAG Studio', refId: 'rag_studio' }
   ]);
   const [ragStudioData, setRagStudioData] = React.useState<RagStudioData | null>(null);
   const [ragEntities, setRagEntities] = React.useState<RagEntityItem[]>([]);
   const [isUploading, setIsUploading] = React.useState(false);
   const [isIndexing, setIsIndexing] = React.useState(false);
   const { setNodes } = useReactFlow();
   const inputRef = React.useRef<HTMLInputElement>(null);
   const edges = useEdges();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   const [urlScrapeMode, setUrlScrapeMode] = React.useState('normal');
   const [activeTab, setActiveTab] = React.useState<'configure' | 'sources' | 'advanced'>('configure');

   const handleConfigChange = (key: string, value: any) => {
      const currentConfig = data.value?.ragConfig || {};
      updateNodeValue(id, setNodes, { ...data.value, ragConfig: { ...currentConfig, [key]: value } });
   };

   const handleDeleteUrl = (urlToDelete: string) => {
      const currentUrls = data.value?.urls || [];
      updateNodeValue(id, setNodes, { ...data.value, urls: currentUrls.filter((u: any) => (typeof u === 'string' ? u : u.url) !== urlToDelete) });
   };

   // Load RAG Studio data
   const loadRagData = React.useCallback(async () => {
      try {
         const response = await axios.get(`${webURL}/rag`);
         const withFileRag = response.data.find((r: any) => r.type === 'with_file');
         if (withFileRag) {
            setRagStudioData({
               id: withFileRag._id,
               isIndexed: withFileRag.isIndexed || false,
               indexingStatus: withFileRag.indexingStatus || 'idle',
               files: withFileRag.files || []
            });
            // Store RAG ID for backend to use
            if (!data.value?.ragStudioId || data.value.ragStudioId !== withFileRag._id) {
               updateNodeValue(id, setNodes, { ragStudioId: withFileRag._id });
            }
         }
      } catch (err) {
         console.error('Error loading RAG Studio data:', err);
      }
   }, [id, data.value?.ragStudioId, setNodes]);

   // Load all RAG entities for RAG Studio selector
   const loadRagEntities = React.useCallback(async () => {
      try {
         const response = await axios.get(`${webURL}/rag`);
         setRagEntities(response.data || []);
      } catch (err) {
         console.error('Error loading RAG entities:', err);
      }
   }, []);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      setRequests(requests => requests.map(request => ({ ...request, isActive: request.refId === (data.value?.request || requests[0].refId) })));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // Load RAG Studio data when With File is selected
   React.useEffect(() => {
      if (data.value?.request === 'with_file') {
         loadRagData();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.request, isExpanded]);

    // Load RAG entities when expanded
    React.useEffect(() => {
       if (isExpanded) {
          loadRagEntities();
       }
       // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

   // ---
   React.useEffect(() => {
      addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
         if (node.id === id) {
            const value = node.data.value ? { ...node.data.value, request: node.data.value?.request || requests[0].refId } : { request: requests[0].refId };
            return { ...node, data: { ...node.data, value } }
         } else return node;
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [edges.length]);

   // ---
   // const handleSelectRequest = (selectId: number) => {
   //    const refId = requests.find(obj => obj.id === selectId)?.refId;

   //    setRequests((requests) => requests.map((request) => {
   //       if (request.id === selectId) return { ...request, isActive: true }; else return { ...request, isActive: false };
   //    }));
   //    setNodes(nodes => nodes.map(node => {
   //       if (node.id === id) {
   //          return { ...node, data: { ...node.data, value: { ...node.data.value, request: refId } } }
   //       } else return node;
   //    }));
   // }

   // // ---
   // const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
   //    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
   // }

   // Handle file upload to RAG Studio
   const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const maxFileSize = 100 * 1024 * 1024; // 100 MB
      let files: File[] = [];

      if (event.type === 'drop') {
         files = Array.from((event as React.DragEvent<HTMLDivElement>).dataTransfer.files);
      } else if (event.type === 'change') {
         const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
         if (changeEvent.target.files) files = Array.from(changeEvent.target.files);
      }

      const validFiles = files.filter((file) => allowedFileTypesDocSearch.includes(file.type) && file.size <= maxFileSize);
      if (!validFiles.length) return;

      setIsUploading(true);
      if (isDragFile) setIsDragFile(false);

      try {
         let ragId: string = ragStudioData?.id || '';

         // Create RAG entry if it doesn't exist
         if (!ragId) {
            const createResponse = await axios.post(`${webURL}/rag`, { type: 'with_file' });
            ragId = createResponse.data._id;
            setRagStudioData({
               id: ragId,
               isIndexed: false,
               indexingStatus: 'idle',
               files: []
            });
            updateNodeValue(id, setNodes, { ragStudioId: ragId });
         }

         // Upload each file: first to storage, then metadata to /rag/file
         for (const file of validFiles) {
            // Step 1: Upload file to storage
            const formData = new FormData();
            formData.append('file', file);
            const uploadResult = await axios.post(`${webURL}/general/uploader`, formData, {
               headers: {
                  'Content-Type': 'multipart/form-data',
                  'x-auth-token': store.getState().user.data?.token
               }
            });

            // Step 2: Add file metadata to RAG
            const newFile = {
               type: file.type,
               name: file.name,
               url: uploadResult.data.url
            };
            await axios.post(`${webURL}/rag/file`, { ragId, file: newFile });
         }

         // Reload data after upload
         await loadRagData();
      } catch (err) {
         console.error('Error uploading files:', err);
      } finally {
         setIsUploading(false);
         if (inputRef.current) inputRef.current.value = '';
      }
   }



   // Handle file delete from RAG Studio
   const handleDeleteFile = async (fileUrl: string) => {
      if (!ragStudioData?.id) return;

      try {
         await axios.delete(`${webURL}/rag/file`, {
            data: { ragId: ragStudioData.id, fileUrl }
         });
         // Reload data after delete
         await loadRagData();
      } catch (err) {
         console.error('Error deleting file:', err);
      }
   }

   // Handle indexing
   const handleStartIndexing = async () => {
      const hasFiles = ragStudioData?.files && ragStudioData.files.length > 0;
      const hasUrls = data.value?.urls && data.value.urls.length > 0;

      if (!hasFiles && !hasUrls) return;

      let ragId = '';
      if (data.value?.request === 'rag_studio') {
         ragId = data.value?.ragCollectionId || '';
         if (!ragId) {
            // Cannot index without selecting a collection in RAG Studio mode
            console.error('No RAG collection selected');
            return;
         }
      } else {
         ragId = ragStudioData?.id || '';
         // Create RAG entry if it doesn't exist (e.g. URL-only indexing)
         if (!ragId) {
            try {
               const createResponse = await axios.post(`${webURL}/rag`, { type: 'unified' });
               ragId = createResponse.data._id;
               setRagStudioData({
                  id: ragId,
                  isIndexed: false,
                  indexingStatus: 'idle',
                  files: []
               });
               updateNodeValue(id, setNodes, { ...data.value, ragStudioId: ragId });
            } catch (err) {
               console.error('Error creating RAG entry:', err);
               return;
            }
         }
      }

      setIsIndexing(true);
      try {
         await axios.post(`${webURL}/rag/index`, {
            ragId: ragId,
            urls: data.value?.urls || [],
            config: data.value?.ragConfig || {}
         });

         // Poll for status
         const pollInterval = setInterval(async () => {
            try {
               const { data: statusData } = await axios.get(`${webURL}/rag/index-status/${ragId}`);
               setRagStudioData(prev => prev ? { ...prev, indexingStatus: statusData.indexingStatus, isIndexed: statusData.indexingStatus === 'completed' } : null);
               if (statusData.indexingStatus === 'completed' || statusData.indexingStatus === 'error') {
                  setIsIndexing(false);
                  clearInterval(pollInterval);
                  await loadRagData();
               }
            } catch (err) {
               clearInterval(pollInterval);
               setIsIndexing(false);
            }
         }, 2000);
      } catch (err) {
         console.error('Error starting indexing:', err);
         setIsIndexing(false);
      }
   }

   // ---
   // const handleFileChange = (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
   //    const maxFileSize = 100 * 1024 * 1024; // 100 MB in bytes
   //    let files: File[] = [];

   //    if (event.type === 'drop') {
   //       files = Array.from((event as React.DragEvent<HTMLDivElement>).dataTransfer.files);

   //    } else if (event.type === 'change') {
   //       const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
   //       if (changeEvent.target.files) files = Array.from(changeEvent.target.files);
   //    }

   //    const arr = files.filter((file) => allowedFileTypesDocSearch.includes(file.type) && file.size <= maxFileSize);
   //    if (arr.length) setNodes((nodes) =>
   //       nodes.map((node) => {
   //          if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, current: node.data.value?.current ? [...node.data.value.current, ...arr] : arr } } };
   //          else return node;
   //       })
   //    );
   //    if (isDragFile) setIsDragFile(false);
   // }

   // ---
   // const handleRemove = (index: number, isFromSave: boolean) => {
   //    if (inputRef.current) inputRef.current.value = '';

   //    if (isFromSave) {
   //       setNodes((nodes) => nodes.map((node) => {
   //          if (node.id === id) {
   //             const arr = [...node.data.value.files];
   //             arr.splice(index, 1);
   //             return { ...node, data: { ...node.data, value: { ...node.data.value, files: arr } } };
   //          } else return node;
   //       }));
   //    } else {
   //       setNodes((nodes) => nodes.map((node) => {
   //          if (node.id === id) {
   //             const arr = [...node.data.value.current];
   //             arr.splice(index, 1);
   //             return { ...node, data: { ...node.data, value: { ...node.data.value, current: arr } } };
   //          } else return node;
   //       }));
   //    }
   // }

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '500px' }}
         >
            <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>

               {/* Status Bar */}
               <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 14px', background: '#fff', border: '1px solid #e5e7eb',
                  borderRadius: '10px', marginBottom: '12px'
               }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                     <div style={{
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: ragStudioData?.isIndexed ? '#10b981' : isIndexing ? '#f59e0b' : '#d1d5db'
                     }} />
                     <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>
                        {ragStudioData?.isIndexed ? 'Ready to Query' : isIndexing ? 'Indexing…' : 'Not Indexed'}
                     </span>
                     {isIndexing && <Loader2 size={13} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />}
                  </div>
                  <button
                     onClick={handleStartIndexing}
                     disabled={isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length)}
                     style={{
                        padding: '6px 14px',
                        background: (isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length))
                           ? '#f3f4f6'
                           : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                        color: (isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length)) ? '#9ca3af' : '#fff',
                        border: 'none', borderRadius: '6px',
                        cursor: (isIndexing || (!ragStudioData?.files?.length && !data.value?.urls?.length)) ? 'not-allowed' : 'pointer',
                        fontSize: '12px', fontWeight: 600, letterSpacing: '0.3px', transition: 'all 0.2s ease'
                     }}
                  >
                     {isIndexing ? 'Processing…' : 'Index Now'}
                  </button>
               </div>

               {/* Tab Navigation */}
               <div style={{
                  display: 'flex', gap: '4px', marginBottom: '12px',
                  background: '#f9fafb', borderRadius: '10px', padding: '4px',
                  border: '1px solid #e5e7eb'
               }}>
                  {([
                     { key: 'configure' as const, label: 'Configure', Icon: Database },
                     { key: 'sources' as const, label: 'Sources', Icon: FolderOpen },
                     { key: 'advanced' as const, label: 'Advanced', Icon: Settings2 }
                  ]).map(({ key, label, Icon }) => (
                     <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        style={{
                           flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                           gap: '5px', padding: '7px 10px',
                           background: activeTab === key ? '#fff' : 'transparent',
                           border: 'none', borderRadius: '7px', cursor: 'pointer',
                           fontSize: '12px', fontWeight: activeTab === key ? 600 : 500,
                           color: activeTab === key ? '#7c3aed' : '#6b7280',
                           boxShadow: activeTab === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                           transition: 'all 0.2s ease'
                        }}
                     >
                        <Icon size={13} />
                        {label}
                     </button>
                  ))}
               </div>

               {/* Tab Content */}
               <div style={{ maxHeight: '420px', overflowY: 'auto', paddingRight: '4px' }}>

                  {/* ── CONFIGURE TAB ── */}
                  {activeTab === 'configure' && <>

                     {/* RAG Entity */}
                     <div style={cardStyle}>
                        <div style={cardHeaderStyle}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <Database size={13} color="#7c3aed" />
                              <span style={labelStyle}>RAG Entity</span>
                           </div>
                           <button onClick={() => loadRagEntities()} style={ghostBtnStyle}>
                              <RefreshCw size={11} />
                              Refresh
                           </button>
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                           <select
                              value={data.value?.ragCollectionId || ''}
                              onChange={(e) => {
                                 const selectedId = e.target.value;
                                 updateNodeValue(id, setNodes, { ...data.value, ragCollectionId: selectedId, request: 'rag_studio' });
                              }}
                              style={selectStyle}
                           >
                              <option value="">— Choose a RAG entity —</option>
                              {ragEntities.map((entity) => (
                                 <option key={entity._id} value={entity._id}>
                                    {entity.name} {entity.isIndexed ? '✓' : '✗'} ({(entity.files?.length || 0) + (entity.urls?.length || 0) + (entity.apis?.length || 0)} sources)
                                 </option>
                              ))}
                           </select>
                           {data.value?.ragCollectionId && (() => {
                              const selected = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                              if (!selected) return null;
                              return (
                                 <div style={{
                                    marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
                                    background: selected.isIndexed ? '#ecfdf5' : '#fff7ed',
                                    border: `1px solid ${selected.isIndexed ? '#a7f3d0' : '#fed7aa'}`
                                 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                       <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{selected.name}</span>
                                       <span style={{
                                          display: 'inline-flex', alignItems: 'center', gap: '3px',
                                          fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
                                          background: selected.isIndexed ? '#10b981' : '#f59e0b',
                                          color: '#fff', fontWeight: 600
                                       }}>
                                          {selected.isIndexed
                                             ? <><CheckCircle2 size={9} /> Indexed</>
                                             : <><XCircle size={9} /> Not Indexed</>}
                                       </span>
                                    </div>
                                    {selected.description && (
                                       <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{selected.description}</div>
                                    )}
                                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                       {selected.files?.length || 0} files · {selected.urls?.length || 0} URLs{(selected.apis?.length || 0) > 0 ? ` · ${selected.apis.length} API${selected.apis.length !== 1 ? 's' : ''}` : ''}
                                    </div>
                                    {!selected.isIndexed && (
                                       <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#ea580c', fontWeight: 500 }}>
                                          <AlertTriangle size={11} />
                                          Index this entity in RAG Studio before querying
                                       </div>
                                    )}
                                 </div>
                              );
                           })()}
                        </div>
                     </div>

                     {/* Question */}
                     <div style={cardStyle}>
                        <div style={cardHeaderStyle}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <MessageSquare size={13} color="#7c3aed" />
                              <span style={labelStyle}>Question</span>
                           </div>
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                           <div style={{ paddingBottom: '6px' }}>
                              <Textarea
                                 value={data.value?.question || ''}
                                 handleChange={(e: any) => updateNodeValue(id, setNodes, { ...data.value, question: e.target.value })}
                                 placeholder="Enter the question to ask against the documents..."
                              />
                           </div>
                           <div style={{ marginTop: '6px', fontSize: '11px', color: '#9ca3af', lineHeight: '1.5' }}>
                              Supports {'{{variables}}'}. Overridden by Input handle if connected.
                           </div>
                        </div>
                     </div>

                     {/* System Instructions */}
                     <div style={cardStyle}>
                        <div style={cardHeaderStyle}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <FileCode size={13} color="#7c3aed" />
                              <span style={labelStyle}>System Instructions</span>
                           </div>
                        </div>
                        <div style={{ padding: '12px 14px' }}>
                           <Textarea
                              value={data.value?.instructions || ''}
                              handleChange={(e: any) => updateNodeValue(id, setNodes, { ...data.value, instructions: e.target.value })}
                              placeholder="Enter instructions for how the AI should use the retrieved context..."
                           />
                        </div>
                     </div>
                  </>}

                  {/* ── SOURCES TAB ── */}
                  {activeTab === 'sources' && <>
                     <div style={cardStyle}>
                        <div style={cardHeaderStyle}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <FolderOpen size={13} color="#7c3aed" />
                              <span style={labelStyle}>Sources</span>
                           </div>
                           <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500 }}>
                              {(() => {
                                 const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                                 const entityFiles = selectedEntity?.files?.length || 0;
                                 const entityUrls = selectedEntity?.urls?.length || 0;
                                 const entityApis = selectedEntity?.apis?.length || 0;
                                 const nodeFiles = ragStudioData?.files?.length || 0;
                                 const nodeUrls = data.value?.urls?.length || 0;
                                 return (entityFiles + entityUrls + entityApis + nodeFiles + nodeUrls) + ' items';
                              })()}
                           </span>
                        </div>

                        {/* Drop Zone */}
                        <div
                           onDragOver={e => { e.preventDefault(); if (!isDragFile) setIsDragFile(true) }}
                           onDragLeave={_ => setIsDragFile(false)}
                           onDrop={handleFileUpload}
                           style={{
                              margin: '12px 14px 0', padding: '20px 16px',
                              border: `2px dashed ${isDragFile ? '#7c3aed' : '#d1d5db'}`,
                              borderRadius: '10px',
                              background: isDragFile ? '#f5f3ff' : '#fafafa',
                              textAlign: 'center', cursor: 'pointer',
                              transition: 'all 0.2s ease', position: 'relative'
                           }}
                        >
                           <UploadCloud size={28} color={isDragFile ? '#7c3aed' : '#9ca3af'} style={{ marginBottom: '8px' }} />
                           <div style={{ fontSize: '13px', color: '#374151', fontWeight: 500 }}>
                              {isUploading ? 'Uploading…' : 'Drop files here or click to upload'}
                           </div>
                           <div style={{ marginTop: '3px', fontSize: '11px', color: '#9ca3af' }}>
                              PDF, Word, Excel, TXT, CSV · Max 100 MB
                           </div>
                           {isUploading && (
                              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                 <Loader2 size={13} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
                                 <span style={{ fontSize: '11px', color: '#7c3aed', fontWeight: 500 }}>Processing…</span>
                              </div>
                           )}
                           <input
                              ref={inputRef}
                              type="file"
                              onChange={handleFileUpload}
                              multiple
                              accept={allowedFileTypesDocSearch.join(',')}
                              disabled={isUploading}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                           />
                        </div>

                        {/* Uploaded Files */}
                        {ragStudioData?.files && ragStudioData.files.length > 0 && (
                           <div style={{ padding: '10px 14px 0' }}>
                              <div style={subLabelStyle}>Uploaded Files</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '150px', overflowY: 'auto', paddingRight: '4px' }}>
                                 {ragStudioData.files.map((file, index) => (
                                    <div key={index} style={fileRowStyle}>
                                       <div style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                                          <FileText size={13} color="#7c3aed" style={{ flexShrink: 0 }} />
                                          <span style={fileNameStyle}>{file.name}</span>
                                       </div>
                                       <button onClick={() => handleDeleteFile(file.url)} style={deleteRowBtnStyle}>
                                          <X size={12} />
                                       </button>
                                    </div>
                                 ))}
                              </div>
                           </div>
                        )}

                        {/* Entity Files (read-only) */}
                        {(() => {
                           const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                           if (!selectedEntity?.files?.length) return null;
                           return (
                              <div style={{ padding: '10px 14px 0' }}>
                                 <div style={subLabelStyle}>Entity Files</div>
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {selectedEntity.files.map((file: any, index: number) => (
                                       <div key={`ef-${index}`} style={{ ...fileRowStyle, background: '#f5f3ff' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                                             <FileText size={13} color="#7c3aed" style={{ flexShrink: 0 }} />
                                             <span style={fileNameStyle}>{file.name || file.url?.split('/').pop() || 'File'}</span>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           );
                        })()}

                        {/* Entity URLs (read-only) */}
                        {(() => {
                           const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                           if (!selectedEntity?.urls?.length) return null;
                           return (
                              <div style={{ padding: '10px 14px 0' }}>
                                 <div style={subLabelStyle}>Entity URLs</div>
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '100px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {selectedEntity.urls.map((urlItem: any, index: number) => {
                                       const url = typeof urlItem === 'string' ? urlItem : urlItem.url;
                                       return (
                                          <div key={`eu-${index}`} style={{ ...fileRowStyle, background: '#eff6ff' }}>
                                             <div style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                                                <Link2 size={13} color="#3b82f6" style={{ flexShrink: 0 }} />
                                                <span style={{ ...fileNameStyle, color: '#2563eb' }}>{url}</span>
                                             </div>
                                          </div>
                                       );
                                    })}
                                 </div>
                              </div>
                           );
                        })()}

                        {/* Entity APIs (read-only) */}
                        {(() => {
                           const selectedEntity = ragEntities.find(e => e._id === data.value?.ragCollectionId);
                           if (!selectedEntity?.apis?.length) return null;
                           return (
                              <div style={{ padding: '10px 14px 0' }}>
                                 <div style={subLabelStyle}>API Sources</div>
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '120px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {selectedEntity.apis.map((api, index) => (
                                       <div key={`ea-${index}`} style={{ ...fileRowStyle, background: '#fdf4ff' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                                             <Zap size={13} color="#7c3aed" style={{ flexShrink: 0 }} />
                                             <div style={{ overflow: 'hidden' }}>
                                                <div style={{ ...fileNameStyle, fontWeight: 600 }}>{api.name}</div>
                                                <div style={{ fontSize: '10px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>{api.url}</div>
                                             </div>
                                          </div>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           );
                        })()}

                        {/* Add URLs */}
                        <div style={{ padding: '10px 14px 14px' }}>
                           <div style={subLabelStyle}>Add URLs</div>
                           <div style={{ display: 'flex', gap: '7px', marginBottom: '7px' }}>
                              <select
                                 value={urlScrapeMode}
                                 onChange={(e) => setUrlScrapeMode(e.target.value as 'normal' | 'vlm')}
                                 style={{ ...selectStyle, width: 'auto', minWidth: '80px', flex: 'none' }}
                              >
                                 <option value="normal">Normal</option>
                                 <option value="vlm">VLM</option>
                              </select>
                              <input
                                 type="text"
                                 placeholder="Enter URL and press Enter…"
                                 onKeyUp={(e) => {
                                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                       const newUrl = e.currentTarget.value.trim();
                                       const currentUrls = data.value?.urls || [];
                                       const exists = currentUrls.some((u: any) => (typeof u === 'string' ? u : u.url) === newUrl);
                                       if (!exists) {
                                          updateNodeValue(id, setNodes, {
                                             ...data.value,
                                             urls: [...currentUrls, {
                                                url: newUrl,
                                                scrapeMode: urlScrapeMode,
                                                scrollCount: urlScrapeMode === 'normal' ? (data.value?.urlScrollCount ?? 7) : undefined,
                                                vlmScreenshots: urlScrapeMode === 'vlm' ? (data.value?.urlVlmScreenshots ?? 10) : undefined
                                             }]
                                          });
                                       }
                                       e.currentTarget.value = '';
                                    }
                                 }}
                                 style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' }}
                              />
                           </div>
                           {urlScrapeMode === 'normal' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                                 <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>Scrolls</label>
                                 <input
                                    type="number" min={0} max={50}
                                    value={data.value?.urlScrollCount ?? 7}
                                    onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, urlScrollCount: Math.max(0, Math.min(50, parseInt(e.target.value) || 0)) })}
                                    style={{ width: '60px', padding: '5px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', background: '#fafafa', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                                 />
                                 <span style={{ fontSize: '10px', color: '#9ca3af' }}>0–50</span>
                              </div>
                           )}
                           {urlScrapeMode === 'vlm' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
                                 <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500, whiteSpace: 'nowrap' }}>Screenshots</label>
                                 <input
                                    type="number" min={1} max={50}
                                    value={data.value?.urlVlmScreenshots ?? 10}
                                    onChange={(e) => updateNodeValue(id, setNodes, { ...data.value, urlVlmScreenshots: Math.max(1, Math.min(50, parseInt(e.target.value) || 1)) })}
                                    style={{ width: '60px', padding: '5px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', background: '#fafafa', outline: 'none', textAlign: 'center', boxSizing: 'border-box' }}
                                 />
                                 <span style={{ fontSize: '10px', color: '#9ca3af' }}>1–50</span>
                              </div>
                           )}
                           {data.value?.urls && data.value.urls.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', maxHeight: '100px', overflowY: 'auto', paddingRight: '4px' }}>
                                 {data.value.urls.map((item: any, index: number) => {
                                    const url = typeof item === 'string' ? item : item.url;
                                    const mode = typeof item === 'string' ? 'normal' : (item.scrapeMode || 'normal');
                                    return (
                                       <div key={index} style={{ ...fileRowStyle, background: '#eff6ff' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', overflow: 'hidden' }}>
                                             <span style={{
                                                fontSize: '9px', padding: '2px 6px', borderRadius: '4px',
                                                background: mode === 'vlm' ? '#7c3aed' : '#3b82f6',
                                                color: '#fff', fontWeight: 700, textTransform: 'uppercase', flexShrink: 0
                                             }}>{mode === 'normal' ? 'Normal' : 'VLM'}</span>
                                             <Link2 size={12} color="#3b82f6" style={{ flexShrink: 0 }} />
                                             <span style={{ ...fileNameStyle, color: '#2563eb' }}>{url}</span>
                                          </div>
                                          <button onClick={() => handleDeleteUrl(url)} style={deleteRowBtnStyle}>
                                             <X size={12} />
                                          </button>
                                       </div>
                                    );
                                 })}
                              </div>
                           )}
                        </div>
                     </div>
                  </>}

                  {/* ── ADVANCED TAB ── */}
                  {activeTab === 'advanced' && <>
                     <div style={cardStyle}>
                        <div style={cardHeaderStyle}>
                           <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                              <Settings2 size={13} color="#7c3aed" />
                              <span style={labelStyle}>RAG Configuration</span>
                           </div>
                        </div>
                        <div style={{ padding: '14px 16px' }}>
                           {/* Models */}
                           <div style={{ marginBottom: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div>
                                 <label style={fieldLabelStyle}>Embedding Model</label>
                                 <select
                                    value={data.value?.ragConfig?.embeddingModel || 'gemini'}
                                    onChange={(e) => handleConfigChange('embeddingModel', e.target.value)}
                                    style={selectStyle}
                                 >
                                    <option value="gemini">Gemini</option>
                                    <option value="openai">OpenAI</option>
                                 </select>
                              </div>
                              <div>
                                 <label style={fieldLabelStyle}>LLM Model</label>
                                 <select
                                    value={data.value?.ragConfig?.llmModel || 'gemini-2.5-flash'}
                                    onChange={(e) => handleConfigChange('llmModel', e.target.value)}
                                    style={selectStyle}
                                 >
                                    <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                                    <option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
                                    <option value="gpt-4o">GPT-4o</option>
                                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                                 </select>
                              </div>
                           </div>
                           {/* Strategy / Retrieval */}
                           <div style={{ marginBottom: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                              <div>
                                 <label style={fieldLabelStyle}>Strategy</label>
                                 <select
                                    value={data.value?.ragConfig?.chunkingStrategy || 'recursive'}
                                    onChange={(e) => handleConfigChange('chunkingStrategy', e.target.value)}
                                    style={selectStyle}
                                 >
                                    <option value="recursive">Recursive</option>
                                    <option value="fixed">Fixed Size</option>
                                    <option value="semantic">Semantic</option>
                                 </select>
                              </div>
                              <div>
                                 <label style={fieldLabelStyle}>Retrieval</label>
                                 <select
                                    value={data.value?.ragConfig?.retrievalMethod || 'vector'}
                                    onChange={(e) => handleConfigChange('retrievalMethod', e.target.value)}
                                    style={selectStyle}
                                 >
                                    <option value="vector">Vector Search</option>
                                    <option value="hybrid">Hybrid Search</option>
                                 </select>
                              </div>
                           </div>
                           {/* Numeric params */}
                           <div style={{ marginBottom: '14px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                              <div>
                                 <label style={fieldLabelStyle}>Top K</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.topK || 5}
                                    onChange={(e) => handleConfigChange('topK', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '13px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' }}
                                 />
                              </div>
                              <div>
                                 <label style={fieldLabelStyle}>Chunk Size</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.chunkSize || 1000}
                                    onChange={(e) => handleConfigChange('chunkSize', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '13px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' }}
                                 />
                              </div>
                              <div>
                                 <label style={fieldLabelStyle}>Overlap</label>
                                 <input
                                    type="number"
                                    value={data.value?.ragConfig?.overlap || 200}
                                    onChange={(e) => handleConfigChange('overlap', parseInt(e.target.value))}
                                    style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '13px', outline: 'none', background: '#fafafa', boxSizing: 'border-box' }}
                                 />
                              </div>
                           </div>
                           {/* AI Reranker */}
                           <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                              <span style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>AI Reranker</span>
                              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', cursor: 'pointer' }}>
                                 <input
                                    type="checkbox"
                                    checked={data.value?.ragConfig?.useReranker ?? true}
                                    onChange={(e) => handleConfigChange('useReranker', e.target.checked)}
                                    style={{ opacity: 0, width: 0, height: 0 }}
                                 />
                                 <span style={{
                                    position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: (data.value?.ragConfig?.useReranker ?? true) ? '#7c3aed' : '#d1d5db',
                                    transition: '0.3s', borderRadius: '24px'
                                 }}>
                                    <span style={{
                                       position: 'absolute', height: '18px', width: '18px',
                                       left: (data.value?.ragConfig?.useReranker ?? true) ? '22px' : '3px',
                                       bottom: '3px', backgroundColor: 'white', transition: '0.3s', borderRadius: '50%',
                                       boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
                                    }} />
                                 </span>
                              </label>
                           </div>
                        </div>
                     </div>
                  </>}

               </div>
            </div>
         </ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
export const handleDocSearchRun = async ({ id, nodes, setNodes, edges, isNeedValue }: {
   id: string, nodes: Node<any>[], setNodes: (payload: Node<any>[] | ((nodes: Node<any>[]) => Node<any>[])) => void,
   edges: Edge<any>[], isNeedValue?: boolean, targetHandle?: string
}) => {
   const item = nodes.find(node => node.id === id) as Node<operationType>;
   if (item.data.value?.isTemporaryMemory) return '';

   const fileURLTargets: operationType[] = [];
   const sources: operationType[] = [];

   for (const edge of edges) {
      if (edge.target === item.id) {
         for (const node of nodes) {
            if (node.id === edge.source) {
               if (edge.targetHandle === 'File-URL' || edge.targetHandle === 'File') fileURLTargets.push({ id: node.id, ...node.data });
            }
         }
      } else if (edge.source === item.id) {
         for (const node of nodes) {
            if (node.id === edge.target) {
               sources.push({ id: node.id, ...node.data });
            }
         }
      }
   }
   if (!sources.length && item.data.value?.request !== 'rag_studio') return '';
   let urls: { type: string, name: string, url: string }[] = [];
   let response = '';

   // Removed early return for rag_studio so files from chat can be uploaded first

   if (item.data.value.request === 'with_file' || item.data.value.request === 'excel_database' || item.data.value.request === 'rag_studio') {
      const isInside = !fileURLTargets.some(target => target.outputTypeSub === 'fileURL');
      let arr = item.data.value?.current ? [...item.data.value.current] : [];

      if (!urls.length) {
         if (item.data.value?.files) urls = item.data.value.files.filter((f: any) => f && f.url);
         for (const index in item.data.value.current) {
            const request = new FormData();
            request.append('file', item.data.value.current[index]);
            const URL = (await axios.post(`${webURL}/general/uploader`, request, { headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': store.getState().user.data?.token } })).data.url;
            urls.push({
               type: item.data.value.current[index].type,
               name: item.data.value.current[index].name,
               url: URL
            });
         }
         item.data.value.current = [];
      }
      arr = [];
      if (!item.data.value) item.data.value = {};

      // Fix state update out of sync issue and repeat run
      item.data.value.files = urls;
      item.data.value.isTemporaryMemory = true;

      addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
         if (item.id === node.id) {
            if (isInside) {
               return { ...node, data: { ...node.data, value: { ...node.data.value, files: urls, current: arr, isTemporaryMemory: true } } };
            } return node;
         }
         else if (sources.some(source => source.id === node.id && source.action === 'output')) return { ...node, data: { ...node.data, value: response } };
         else return node;
      }));
      
      if (item.data.value.request === 'rag_studio') {
         if (isNeedValue) return response;
         return;
      }
      if (isNeedValue) return response;
   }
}